import type { ActivityType } from "../types/activity.types";
import { axiosClient } from "../api/axiosClient";
import type {
  ActivityChatSummary,
  ActivityStructuredSelectionPayload,
  BankAccountChatSummary,
  EventChatSummary,
  RitualBathChatSummary,
  EventStructuredSelectionPayload,
  FinancialChatSummary,
  FormChatFieldInput,
  FormChatKeyValueSummary,
  MonthlyFeeChatSummary,
  FormChatSelectionPayload,
  SpiritualChatSummary,
} from "gsintegrations";

export type FormChatStep =
  | "basic-info"
  | "quantity"
  | "location"
  | "template"
  | "time-selection"
  | "checkin-window"
  | "activity-selection"
  | "members-selection"
  | "insumos-selection"
  | "financial"
  | "ingredients"
  | "ritual-steps"
  | "review"
  | "complete";

export interface RitualBathChatIngredientState {
  name: string;
  quantity?: string;
  unit?: string;
  isOptional?: boolean;
  notes?: string;
}

export interface RitualBathChatStepState {
  text: string;
  type: "INGREDIENT" | "PREPARATION" | "APPLICATION" | "TIP";
  icon?: string;
}

export interface FormChatState {
  name?: string;
  category?: string;
  subcategory?: string;
  description?: string;
  quantity?: number;
  minimumQuantity?: number;
  estimatedUnitCost?: number | "skipped";
  unit?: string;
  location?: string | Record<string, unknown>;
  /** Inventory photo step; the file itself stays client-side until creation. */
  image?: "attached" | "skipped";
  imageUrl?: string | null;
  title?: string;
  type?: string;
  activityType?: "ACTIVITY" | "RITUAL";
  typeCustomLabel?: string;
  terreiroId?: string;
  isTemplate?: boolean;
  isInTerreiro?: boolean;
  date?: string;
  isAllDay?: boolean;
  startTime?: string;
  endTime?: string;
  qrCheckinEnabled?: boolean;
  checkinStartWindowMinutes?: number;
  checkinEndWindowMinutes?: number;
  memberIds?: string[];
  minConfirmedMembers?: number;
  insumos?: Array<{ id: string; quantity: number }>;
  activities?: Array<{
    id: string;
    startTime?: string;
    endTime?: string;
    required?: boolean;
    checkinStartWindowMinutes?: number;
    checkinEndWindowMinutes?: number;
  }>;
  requiredActivityIds?: string[];
  memberNames?: string[];
  insumoLabels?: string[];
  lineId?: string;
  lineName?: string;
  entryMode?: "line" | "entity";
  entityId?: string;
  entityName?: string;
  developmentStage?: string;
  visibility?: string;
  notes?: string;
  amount?: number;
  categoryId?: string;
  categoryName?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  accountId?: string;
  accountName?: string;
  paymentMethod?: string;
  settlementStatus?: "PROJECTED" | "CONSOLIDATED";
  dueDate?: string;
  isRecurring?: boolean;
  installments?: number;
  inventoryItemId?: string;
  inventoryItemName?: string;
  purchasedQuantity?: number;
  isSharedWithMembers?: boolean;
  shareReason?: string;
  observation?: string;
  correctionReason?: string;
  monthlyFee?: number;
  dueDay?: number;
  receivingAccountId?: string;
  receivingAccountName?: string;
  accountType?: "BANK" | "CASH";
  bankAccountOptionId?: string;
  bankName?: string;
  agency?: string;
  account?: string;
  pixKeyType?: "PHONE" | "CPF" | "CNPJ" | "EMAIL" | "RANDOM";
  pixKey?: string;
  currentBalance?: number;
  bathCategoryId?: string;
  bathCategoryName?: string;
  bathTitle?: string;
  bathDescription?: string | null;
  bathIntroduction?: string | null;
  bathSource?: string | null;
  bathImage?: "attached" | "skipped";
  bathImageUrl?: string | null;
  bathIngredients?: RitualBathChatIngredientState[];
  ingredientsDone?: boolean;
  ingredientsAddingMore?: boolean;
  bathSteps?: RitualBathChatStepState[];
  stepsDone?: boolean;
  stepsAddingMore?: boolean;
  bathStatus?: "ACTIVE" | "DRAFT" | "INACTIVE";
  scheduleOnCalendar?: boolean;
  scheduleDate?: string;
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  hasExistingSchedule?: boolean;
}

export interface FinancialReferenceData {
  financialCategories: Array<{
    id: string;
    name: string;
    subcategories?: Array<{ id: string; name: string }>;
  }>;
  bankAccounts?: Array<{ id: string; name: string }>;
  inventoryItems?: Array<{ id: string; name: string; unit?: string }>;
  canShareWithMembers?: boolean;
  prefillInventoryItemId?: string;
}

export interface MonthlyFeeReferenceData {
  bankAccounts: Array<{ id: string; name: string }>;
}

export interface BankAccountReferenceData {
  bankOptions: Array<{ id: string; name: string }>;
  hasCashAccount?: boolean;
}

export interface RitualBathReferenceData {
  bathCategories: Array<{ id: string; name: string }>;
}

export interface ActivityReferenceData {
  members?: Array<{ id: string; name: string }>;
  insumos?: Array<{
    id: string;
    name: string;
    availableQuantity: number;
    unit?: string;
  }>;
  ownerMediumId?: string;
  canCreateModels?: boolean;
}

export interface EventReferenceData {
  members?: Array<{ id: string; name: string }>;
  insumos?: Array<{
    id: string;
    name: string;
    availableQuantity: number;
    unit?: string;
  }>;
  activities?: Array<{ id: string; title: string; status?: string }>;
  eventTypes?: Array<{ value: string; label: string }>;
  canCreateTemplates?: boolean;
  isTerreiroAddressEmpty?: boolean;
}

export interface SpiritualReferenceData {
  lines?: Array<{ id: string; name: string }>;
  entities?: Array<{ id: string; name: string; lineId?: string }>;
}

export interface FormChatMessageRequest {
  sessionId?: string;
  entityType:
    | "inventory_item"
    | "activity"
    | "event"
    | "spiritual_profile"
    | "event_publication"
    | "income"
    | "outcome"
    | "monthly_fee"
    | "bank_account"
    | "ritual_bath";
  message: string;
  formState?: FormChatState;
  action?: "confirm" | "edit_field" | "resume";
  editField?: string;
  mode?: "create" | "edit" | "from_model";
  targetEntityId?: string;
  /** Activity flavor; the API picks wording from it and never asks the user. */
  activityType?: ActivityType;
  eventId?: string;
  knownLocations?: string[];
  knownCategories?: string[];
  selection?: FormChatSelectionPayload;
  structuredSelection?:
    | ActivityStructuredSelectionPayload
    | EventStructuredSelectionPayload;
  referenceData?:
    | ActivityReferenceData
    | EventReferenceData
    | SpiritualReferenceData
    | FinancialReferenceData
    | MonthlyFeeReferenceData
    | BankAccountReferenceData
    | RitualBathReferenceData;
  isPersonalProfile?: boolean;
  inputMode?: "text" | "voice";
}

export interface FormChatTranscribeResponse {
  transcript: string;
}

export interface FormChatMessageResponse {
  sessionId: string;
  assistantMessage: string;
  currentStep: FormChatStep;
  formState: FormChatState;
  isComplete: boolean;
  quickReplies?: string[];
  activeField?: string;
  fieldInput?: FormChatFieldInput;
  validationErrors?: Record<string, string>;
  summary?:
    | FormChatState
    | ActivityChatSummary
    | EventChatSummary
    | SpiritualChatSummary
    | FinancialChatSummary
    | MonthlyFeeChatSummary
    | BankAccountChatSummary
    | RitualBathChatSummary;
  descriptionAutoGenerated?: boolean;
  mode?: "create" | "edit" | "from_model";
  targetEntityId?: string;
  dirtyFields?: string[];
  options?: Array<{ id: string; label: string; disabled?: boolean }>;
  ticket?: {
    code: string;
    managementToken: string;
    voucherUrl?: string;
  };
}

export type ProfileFormChatEntityType =
  | "address"
  | "contact"
  | "personal_profile"
  | "terreiro_profile"
  | "profile_avatar"
  | "terreiro_avatar"
  | "medium_data"
  | "credentials";

export type ProfileFormChatScope =
  | { kind: "personal"; mediumId: string }
  | { kind: "terreiro"; terreiroId: string };

export type ProfileChatFormState = Record<string, string | boolean | null>;

export interface ProfileFormChatRequest {
  sessionId?: string;
  entityType: ProfileFormChatEntityType;
  message: string;
  action?: "confirm" | "edit_field" | "resume" | "undo" | "skip";
  editField?: string;
  selection?: FormChatSelectionPayload;
  targetEntityId?: string;
  inputMode?: "text" | "voice";
}

export interface ProfileFormChatResponse {
  sessionId: string;
  assistantMessage: string;
  currentStep: string;
  formState: ProfileChatFormState;
  isComplete: boolean;
  quickReplies?: string[];
  activeField?: string;
  fieldInput?: FormChatFieldInput;
  options?: Array<{ id: string; label: string; disabled?: boolean }>;
  summary?: FormChatKeyValueSummary;
  validationErrors?: Record<string, string>;
  mode?: "create" | "edit";
  targetEntityId?: string;
  dirtyFields?: string[];
}

const profileScopeBase = (scope: ProfileFormChatScope) =>
  scope.kind === "terreiro"
    ? `/terreiros/${scope.terreiroId}/form-chat`
    : `/mediums/${scope.mediumId}/form-chat`;

const postMessage = async (
  url: string,
  payload: FormChatMessageRequest,
): Promise<FormChatMessageResponse> => {
  const response = await axiosClient.post(url, payload, {
    withCredentials: true,
  });
  return response.data as FormChatMessageResponse;
};

export const formChatRepository = {
  async sendMessage(
    terreiroId: string,
    payload: FormChatMessageRequest,
  ): Promise<FormChatMessageResponse> {
    return postMessage(`/terreiros/${terreiroId}/form-chat/message`, payload);
  },

  async sendPersonalMessage(
    mediumId: string,
    payload: FormChatMessageRequest,
  ): Promise<FormChatMessageResponse> {
    return postMessage(`/mediums/${mediumId}/form-chat/message`, payload);
  },

  async transcribeAudio(
    terreiroId: string,
    audio: Blob,
    scope?: {
      entityType: "income" | "outcome" | "monthly_fee" | "bank_account" | "ritual_bath";
      mode?: "create" | "edit";
    },
  ): Promise<FormChatTranscribeResponse> {
    const formData = new FormData();
    formData.append("audio", audio, "recording.webm");

    const response = await axiosClient.post(
      `/terreiros/${terreiroId}/form-chat/transcribe`,
      formData,
      {
        withCredentials: true,
        ...(scope ? { params: scope } : {}),
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 45_000,
      },
    );

    return response.data as FormChatTranscribeResponse;
  },

  async sendProfileMessage(
    scope: ProfileFormChatScope,
    payload: ProfileFormChatRequest,
  ): Promise<ProfileFormChatResponse> {
    const response = await axiosClient.post(
      `${profileScopeBase(scope)}/message`,
      payload,
      { withCredentials: true },
    );
    return response.data as ProfileFormChatResponse;
  },

  async transcribeProfileAudio(
    scope: ProfileFormChatScope,
    entityType: ProfileFormChatEntityType,
    audio: Blob,
  ): Promise<FormChatTranscribeResponse> {
    const formData = new FormData();
    formData.append("audio", audio, "recording.webm");
    const response = await axiosClient.post(
      `${profileScopeBase(scope)}/transcribe`,
      formData,
      {
        withCredentials: true,
        params: { entityType },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45_000,
      },
    );
    return response.data as FormChatTranscribeResponse;
  },

  async transcribePersonalAudio(
    mediumId: string,
    audio: Blob,
  ): Promise<FormChatTranscribeResponse> {
    const formData = new FormData();
    formData.append("audio", audio, "recording.webm");

    const response = await axiosClient.post(
      `/mediums/${mediumId}/form-chat/transcribe`,
      formData,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 45_000,
      },
    );

    return response.data as FormChatTranscribeResponse;
  },
};
